// src/app/components/chat/chatUtils.ts
//
// Helpers de UI compartilhados entre os componentes de chat interno.
// Nenhuma chamada de API aqui — apenas formatação/derivação de dados
// já carregados pelos componentes.

import { File as FileIcon, FileSpreadsheet, FileText, Music, Video } from 'lucide-react';
import type { ElementType } from 'react';
import type { ChatAttachmentCategory, ChatUserMini } from '@/app/services/chatService';

/** Iniciais para o AvatarFallback — "João Silva" → "JS", fallback no username. */
export function getUserInitials(user: ChatUserMini | null | undefined): string {
  if (!user) return '?';
  const parts = user.full_name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return user.username.slice(0, 2).toUpperCase();
}

/** "14:32" para mensagens de hoje, "23/07" para mensagens de outros dias. */
export function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (sameDay) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** Label de separador de data — "Hoje", "Ontem" ou "23 de julho de 2026". */
export function formatDaySeparator(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const isSameDate = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  if (isSameDate(d, now)) return 'Hoje';
  if (isSameDate(d, yesterday)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Mesma regra de StorageFile.category (backend) — usada no composer, antes
 * do servidor confirmar a categoria do anexo (preview local de arquivo
 * ainda não enviado). */
export function categoryFromMime(mimeType: string): ChatAttachmentCategory {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'file';
}

/** "1.4 MB" — mesmo formato de StorageFile.size_formatted (backend), usado
 * antes do upload terminar (ainda não temos o StorageFile de volta). */
export function formatFileSize(bytes: number): string {
  let size = bytes;
  for (const unit of ['B', 'KB', 'MB', 'GB']) {
    if (size < 1024) return `${size.toFixed(1)} ${unit}`;
    size /= 1024;
  }
  return `${size.toFixed(1)} TB`;
}

/** Ícone + cor pra chip de anexo genérico (categoria 'file' ou 'video'/
 * 'audio' sem preview disponível) — mesmo critério de AttachFileMenu.tsx
 * (chat ao vivo do WhatsApp), reaproveitado aqui pro chat interno. */
export function fileIconFor(mimeType: string, name: string): { Icon: ElementType; className: string } {
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (mimeType.startsWith('video/')) return { Icon: Video, className: 'text-violet-500' };
  if (mimeType.startsWith('audio/')) return { Icon: Music, className: 'text-pink-500' };
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return { Icon: FileSpreadsheet, className: 'text-emerald-600' };
  if (ext === 'docx' || ext === 'doc' || ext === 'pdf') return { Icon: FileText, className: 'text-blue-600' };
  return { Icon: FileIcon, className: 'text-slate-500' };
}
